import { SelectOption } from "src/store/analytics/storeAnalytics.types";

interface FormDataHelpers {
  toFormattedSelectOptions: (item: any) => SelectOption | null;
  removeDuplicateOptions: () => (opt: SelectOption) => boolean;
  getFacilityLabel: (facility: SelectOption[] | null | undefined) => string;
  getFacilityValue: (facility: SelectOption[] | null | undefined) => string;
}

const useFormDataHelpers = (): FormDataHelpers => {
  /**
   * Normalizes various API response shapes into a SelectOption format.
   * Handles: strings.
   */
  const toFormattedSelectOptions = (item: any): SelectOption | null => {
    if (item && typeof item === "string") {
      return { label: item, value: item };
    }
    return null;
  };

  /**
   * Factory function that returns a filter callback to remove duplicates by value.
   * Creates a fresh Set for each call, keeping the first occurrence of each value.
   * Usage: .filter(removeDuplicateOptions())
   */
  const removeDuplicateOptions = () => {
    const seenValues = new Set<string>();
    return (opt: SelectOption) => {
      if (seenValues.has(opt.value)) return false;
      else seenValues.add(opt.value);
      return true;
    };
  };

  /**
   * Safely extracts facility label from analytics state
   * Returns empty string if facility is null/undefined/empty
   */
  const getFacilityLabel = (
    facility: SelectOption[] | null | undefined
  ): string => {
    return facility?.[0]?.label ?? "";
  };

  /**
   * Safely extracts facility value from analytics state
   * Returns empty string if facility is null/undefined/empty
   */
  const getFacilityValue = (
    facility: SelectOption[] | null | undefined
  ): string => {
    return facility?.[0]?.value ?? "";
  };

  return {
    toFormattedSelectOptions,
    removeDuplicateOptions,
    getFacilityLabel,
    getFacilityValue,
  };
};

export default useFormDataHelpers;
